import { formatarDocumento } from '@/lib/formato';
import {
  cnpjSchema,
  type CondicoesRegra,
  type MODELOS_REGRA_CLASSIFICACAO,
} from '@fiscoai/contracts';

/** As quatro condições de uma regra, na ordem em que aparecem na frase. */
export type TipoCondicao = keyof CondicoesRegra;
export const TIPOS_CONDICAO: readonly TipoCondicao[] = [
  'ncmPrefixos',
  'cfopsFornecedor',
  'cnpjsEmitente',
  'descricaoContem',
];

/** "2710125" → "2710.12.5": os grupos da TIPI (4.2.2), mesmo com prefixo incompleto. */
export function formatarNcm(digitos: string): string {
  return [digitos.slice(0, 4), digitos.slice(4, 6), digitos.slice(6, 8)]
    .filter((g) => g !== '')
    .join('.');
}

/** "2710.12.59" → "27101259"; fora de 2 a 8 dígitos, recusa. */
export function interpretarNcm(texto: string): string | null {
  const d = texto.replace(/[\s.-]/g, '');
  return /^\d{2,8}$/.test(d) ? d : null;
}

/** "5.656" → "5656"; só CFOP de saída (5, 6 ou 7), o do fornecedor. */
export function interpretarCfop(texto: string): string | null {
  const d = texto.replace(/[\s.]/g, '');
  return /^[5-7]\d{3}$/.test(d) ? d : null;
}

/** CNPJ com ou sem máscara (inclusive alfanumérico); o dígito verificador é conferido. */
export function interpretarCnpj(texto: string): string | null {
  const limpo = texto.replace(/[\s./-]/g, '').toUpperCase();
  return cnpjSchema.safeParse(limpo).success ? limpo : null;
}

/** Trecho de descrição: 2 a 60 caracteres. */
export function interpretarDescricao(texto: string): string | null {
  const t = texto.trim();
  return t.length >= 2 && t.length <= 60 ? t : null;
}

interface MetaCondicao {
  /** Como entra na frase: "Quando o NCM começar com …". */
  frase: string;
  /** Opção do seletor de condição no editor. */
  opcao: string;
  interpretar: (texto: string) => string | null;
  formatar: (valor: string) => string;
  mono: boolean;
  separador: RegExp;
  placeholder: string;
}

const SEPARADOR_CODIGOS = /[\s;,]+/;

export const CONDICAO: Record<TipoCondicao, MetaCondicao> = {
  ncmPrefixos: {
    frase: 'o NCM começar com',
    opcao: 'NCM começa com',
    interpretar: interpretarNcm,
    formatar: formatarNcm,
    mono: true,
    separador: SEPARADOR_CODIGOS,
    placeholder: '2710.12.59 ou cole uma lista',
  },
  cfopsFornecedor: {
    frase: 'o CFOP do fornecedor for',
    opcao: 'CFOP do fornecedor é',
    interpretar: interpretarCfop,
    formatar: (v) => v,
    mono: true,
    separador: SEPARADOR_CODIGOS,
    placeholder: '5656, 5929…',
  },
  cnpjsEmitente: {
    frase: 'o emitente for',
    opcao: 'Emitente (CNPJ) é',
    interpretar: interpretarCnpj,
    formatar: formatarDocumento,
    mono: true,
    separador: SEPARADOR_CODIGOS,
    placeholder: 'CNPJ do fornecedor',
  },
  descricaoContem: {
    frase: 'a descrição tiver',
    opcao: 'Descrição contém',
    interpretar: interpretarDescricao,
    formatar: (v) => `“${v}”`,
    mono: false,
    separador: /[\n;,]+/,
    placeholder: 'gasolina; diesel',
  },
};

/** Até `mostrar` valores formatados e quantos sobraram ("… (+4)"). */
export function resumirValores(
  valores: readonly string[],
  formatar: (v: string) => string,
  mostrar = 3,
): { visiveis: string[]; restantes: number } {
  return {
    visiveis: valores.slice(0, mostrar).map(formatar),
    restantes: Math.max(0, valores.length - mostrar),
  };
}

/** "1 item" / "47 itens". */
export function itens(n: number): string {
  return `${n.toLocaleString('pt-BR')} ${n === 1 ? 'item' : 'itens'}`;
}

/** Condições que têm valor, na ordem da frase. */
export function condicoesPreenchidas(condicoes: CondicoesRegra): TipoCondicao[] {
  return TIPOS_CONDICAO.filter((t) => condicoes[t].length > 0);
}

/** Rótulo do modelo no menu "Usar um modelo" (o que ele pega, curto). */
export const ROTULO_MODELO: Record<(typeof MODELOS_REGRA_CLASSIFICACAO)[number]['id'], string> = {
  combustiveis: 'Combustíveis (gasolina, etanol, diesel, GNV)',
};
